import { arrayUpdateInPlace, moveToFulfilled, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { AddGroupLeaderFulfilledAction } from '../../domains/groupAddRemoveLeader/action.types';
import type {
  GroupAddUserFulfilledAction,
  GroupRemoveUserFulfilledAction,
} from '../../domains/groupAddRemoveUser/actions.types';
import type {
  GroupMakeAdminFulfilledAction,
  GroupRevokeAdminFulfilledAction,
} from '../../domains/groupMakeRevokeAdmin/actions.types';
import type { ResolveGroupMembershipFromMembershipPanelFulfilledAction } from '../../domains/groupResolveMembershipRequest/actions.types';
import type { GroupsMembersLoadFulfilledAction } from '../../domains/groupsMembers/actions.types';
import type { Group, GroupMembersEdge, User } from '../types';

import type { GroupAboutMembers, GroupsAboutMembers } from './types';

export type GroupsMembersAction =
  | GroupsMembersLoadFulfilledAction
  | ResolveGroupMembershipFromMembershipPanelFulfilledAction
  | GroupAddUserFulfilledAction
  | GroupRemoveUserFulfilledAction
  | GroupMakeAdminFulfilledAction
  | GroupRevokeAdminFulfilledAction
  | AddGroupLeaderFulfilledAction;

const initialState: GroupsAboutMembers = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithUpdatedFulfilledGroupMembers = (
  state: GroupsAboutMembers,
  id: Group['id'],
  updatedFields: Partial<GroupAboutMembers>
) => update(state, id, updatedFields);

const filterMembersNotIncludingUser = (edges: GroupMembersEdge[], userId: User['id']) =>
  edges.filter((edge) => edge.nodeId !== userId);

const memberEdgeCompare = (a: GroupMembersEdge, b: GroupMembersEdge) => a.nodeId === b.nodeId;

const makeMemberGroupAdmin = (
  state: GroupsAboutMembers,
  { groupId, userId }: GroupMakeAdminFulfilledAction['payload']
) => {
  const groupAboutMembers = state.fulfilled.byId[groupId];
  if (!groupAboutMembers) {
    return state;
  }

  const { adminMembersEdges, membersEdges } = groupAboutMembers;
  const newGroupAdmin: GroupMembersEdge = {
    isAdmin: true,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupAboutMembers,
    adminMembersEdges: arrayUpdateInPlace<GroupMembersEdge>(adminMembersEdges, newGroupAdmin, memberEdgeCompare),
    membersEdges: filterMembersNotIncludingUser(membersEdges, userId),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const revokeMembersGroupAdminStatus = (
  state: GroupsAboutMembers,
  { groupId, userId }: GroupMakeAdminFulfilledAction['payload']
) => {
  const groupAboutMembers = state.fulfilled.byId[groupId];
  if (!groupAboutMembers) {
    return state;
  }

  // oxlint-disable-next-line typescript/no-useless-default-assignment -- Communities team will need to fix this
  const { adminMembersEdges, membersEdges = [] } = groupAboutMembers;
  const newGroupMember: GroupMembersEdge = {
    isAdmin: false,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupAboutMembers,
    adminMembersEdges: [...filterMembersNotIncludingUser(adminMembersEdges, userId)],
    membersEdges: arrayUpdateInPlace<GroupMembersEdge>(membersEdges, newGroupMember, memberEdgeCompare),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const addMemberToGroup = (state: GroupsAboutMembers, { groupId, userId }: GroupAddUserFulfilledAction['payload']) => {
  const groupAboutMembers = state.fulfilled.byId[groupId];
  if (!groupAboutMembers) {
    return state;
  }

  // oxlint-disable-next-line typescript/no-useless-default-assignment -- Communities team will need to fix this
  const { adminMembersEdges, membersEdges = [] } = groupAboutMembers;
  const newGroupMember: GroupMembersEdge = {
    isAdmin: false,
    nodeId: userId,
  };

  const updatedGroupMembers = {
    ...groupAboutMembers,
    adminMembersEdges: [...filterMembersNotIncludingUser(adminMembersEdges, userId)],
    membersEdges: [...membersEdges, newGroupMember],
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const removeMemberFromGroup = (
  state: GroupsAboutMembers,
  { groupId, userId }: GroupRemoveUserFulfilledAction['payload']
) => {
  const groupAboutMembers = state.fulfilled.byId[groupId];
  if (!groupAboutMembers) {
    return state;
  }

  // oxlint-disable-next-line typescript/no-useless-default-assignment -- Communities team will need to fix this
  const { adminMembersEdges, membersEdges = [] } = groupAboutMembers;

  const updatedGroupMembers = {
    ...groupAboutMembers,
    adminMembersEdges: filterMembersNotIncludingUser(adminMembersEdges, userId),
    membersEdges: filterMembersNotIncludingUser(membersEdges, userId),
  };

  return stateWithUpdatedFulfilledGroupMembers(state, groupId, updatedGroupMembers);
};

const handleFulfilledMembershipRequest = (
  state: GroupsAboutMembers,
  payload: ResolveGroupMembershipFromMembershipPanelFulfilledAction['payload']
) => {
  const membershipRequestType = payload.requestType;

  return membershipRequestType === 'approve' ? addMemberToGroup(state, payload) : state;
};

const reducer: Reducer<GroupsAboutMembers, GroupsMembersAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'GROUPS_MEMBERS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.groupAboutMembers);
    case 'GROUP.RESOLVE_MEMBERSHIP_REQUEST_FULFILLED':
      return handleFulfilledMembershipRequest(state, action.payload);
    case 'GROUP.ADD_USER_FULFILLED':
      return addMemberToGroup(state, action.payload);
    case 'GROUP.REMOVE_USER_FULFILLED':
      return removeMemberFromGroup(state, action.payload);
    case 'GROUP.MAKE_ADMIN_FULFILLED':
    case 'GROUP.ADD_LEADER_FULFILLED':
      return makeMemberGroupAdmin(state, action.payload);
    case 'GROUP.REVOKE_ADMIN_FULFILLED':
      return revokeMembersGroupAdminStatus(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
